import { getError } from '@autobid/strapi-integration/utils/getError'
import { CLIENT_IGNORE_ERRORS } from './consts/CLIENT_IGNORE_ERRORS'

export default async () => {
  const {
    VERSION,
    ui: { theme },
    SENTRY: { ENVIRONMENT: environment, DSN: dsn, REPLAYS, REPORT_DIALOG, BROWSER_TRACING, BROWSER_PROFILING, DISABLED },
  } = useRuntimeConfig().public

  if (dsn && environment && !Number(DISABLED)) {
    const areReplaysEnabled = Boolean(Number(REPLAYS))
    const isReportingEnabled = Boolean(Number(REPORT_DIALOG))
    const isBrowserTracingEnabled = Boolean(Number(BROWSER_TRACING))
    const isBrowserProfilingEnabled = Boolean(Number(BROWSER_PROFILING))
    const release = `${theme}@${VERSION}`

    const {
      init,
      browserSessionIntegration,
      browserTracingIntegration,
      httpClientIntegration,
      vueIntegration,
      addIntegration,
    } = await import('@sentry/nuxt')

    init({
      dsn,
      release,
      environment,
      sampleRate: 1.0,
      tracesSampleRate: 1.0,
      ...(isBrowserProfilingEnabled && { profilesSampleRate: 1.0 }),
      ignoreErrors: CLIENT_IGNORE_ERRORS,
      denyUrls: [/extension\//i, /^chrome:\/\//i, /cdn\.cookielaw\.org/],
      beforeSend(event, hint) {
        const IGNORE_CODES = [401, 403, 404, 422, 503]
        if (!hint.syntheticException) {
          return event
        }

        const statusCode = getError(hint.originalException)?.code

        if (statusCode && IGNORE_CODES.includes(statusCode)) {
          return null
        }

        return event
      },
      integrations: [
        browserSessionIntegration(),
        httpClientIntegration(),
        vueIntegration({
          tracingOptions: {
            trackComponents: true,
            hooks: ['activate', 'create', 'destroy', 'mount', 'update'],
          },
        }),
        browserTracingIntegration({
          instrumentNavigation: isBrowserTracingEnabled,
          instrumentPageLoad: isBrowserTracingEnabled,
        }),
      ],
      ...(areReplaysEnabled && {
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1,
      }),
    })

    const loadHeavyIntegrations = async () => {
      if (isBrowserProfilingEnabled) {
        const { browserProfilingIntegration } = await import('@sentry/nuxt')
        addIntegration(browserProfilingIntegration())
      }

      if (areReplaysEnabled) {
        const { replayIntegration } = await import('@sentry/nuxt')
        addIntegration(replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }))
      }

      if (isReportingEnabled) {
        const { feedbackIntegration, reportingObserverIntegration } = await import('@sentry/nuxt')
        addIntegration(reportingObserverIntegration())
        addIntegration(feedbackIntegration({
          showBranding: false,
          colorScheme: 'system',
          submitButtonLabel: 'Submit',
          messagePlaceholder: 'Please describe the issue',
          isRequiredLabel: '(*)',
        }))
      }
    }

    if (isBrowserProfilingEnabled || areReplaysEnabled || isReportingEnabled) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => loadHeavyIntegrations(), { timeout: 5000 })
      }
      else {
        setTimeout(loadHeavyIntegrations, 3000)
      }
    }
  }
}
